import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';

export type WikiTextBlock = { id: string; type: 'text'; text: string };
export type WikiImageBlock = { id: string; type: 'image'; url: string; caption: string };
export type WikiCalloutBlock = { id: string; type: 'callout'; tone: 'info' | 'warning' | 'success'; text: string };
export type WikiStepsBlock = { id: string; type: 'steps'; items: { title: string; text: string }[] };
export type WikiModelBlock = { id: string; type: 'model'; assetId: string; caption: string };
export type WikiContentBlock = WikiTextBlock | WikiImageBlock | WikiCalloutBlock | WikiStepsBlock | WikiModelBlock;
export type WikiHeading = { id: string; text: string; depth: number };

let blockSequence = 0;
const blockId = () => `wiki-block-${Date.now()}-${++blockSequence}`;
export const createWikiTextBlock = (text = ''): WikiTextBlock => ({ id: blockId(), type: 'text', text });
export const createWikiImageBlock = (url = '', caption = ''): WikiImageBlock => ({ id: blockId(), type: 'image', url, caption });
export function createWikiBlock(type: WikiContentBlock['type']): WikiContentBlock {
  if (type === 'image') return createWikiImageBlock();
  if (type === 'model') return { id: blockId(), type, assetId: '', caption: '' };
  if (type === 'callout') return { id: blockId(), type, tone: 'info', text: '' };
  if (type === 'steps') return { id: blockId(), type, items: [{ title: '', text: '' }] };
  return createWikiTextBlock();
}

export function parseWikiContent(content: string): WikiContentBlock[] {
  try {
    const parsed: unknown = JSON.parse(content);
    if (parsed && typeof parsed === 'object' && 'version' in parsed && parsed.version === 1 && 'blocks' in parsed && Array.isArray(parsed.blocks)) {
      return parsed.blocks.flatMap((value: unknown): WikiContentBlock[] => {
        if (!value || typeof value !== 'object' || !('type' in value)) return [];
        const block = value as Record<string, unknown>;
        if (block.type === 'text' && typeof block.text === 'string') return [createWikiTextBlock(block.text)];
        if (block.type === 'image' && typeof block.url === 'string') return [createWikiImageBlock(block.url, typeof block.caption === 'string' ? block.caption : '')];
        if (block.type === 'model' && typeof block.assetId === 'string') return [{ id: blockId(), type: 'model', assetId: block.assetId, caption: typeof block.caption === 'string' ? block.caption : '' }];
        if (block.type === 'callout' && typeof block.text === 'string') {
          return [{ id: blockId(), type: 'callout', tone: block.tone === 'warning' || block.tone === 'success' ? block.tone : 'info', text: block.text }];
        }
        if (block.type === 'steps' && Array.isArray(block.items)) {
          const items = block.items.flatMap((item: unknown) => {
            if (!item || typeof item !== 'object' || !('title' in item) || typeof item.title !== 'string') return [];
            return [{ title: item.title, text: 'text' in item && typeof item.text === 'string' ? item.text : '' }];
          });
          return [{ id: blockId(), type: 'steps', items }];
        }
        return [];
      });
    }
  } catch {
    // Original articles are stored as plain text/Markdown.
  }
  return [createWikiTextBlock(content)];
}

export function serializeWikiContent(blocks: WikiContentBlock[]): string {
  return JSON.stringify({ version: 1, blocks: blocks.map(({ id: _id, ...block }) => block) });
}

export function isWikiImageUrl(value: string): boolean {
  const url = value.trim();
  if (url.startsWith('/') && !url.startsWith('//') && !url.includes('\\')) return true;
  try { return ['https:', 'http:'].includes(new URL(url).protocol); } catch { return false; }
}

export function hasWikiContent(blocks: WikiContentBlock[]): boolean {
  return blocks.some((block) => {
    if (block.type === 'text' || block.type === 'callout') return Boolean(block.text.trim());
    if (block.type === 'steps') return block.items.some((item) => Boolean(item.title.trim()));
    if (block.type === 'model') return isWikiModelId(block.assetId);
    return isWikiImageUrl(block.url);
  });
}

export const isWikiModelId = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);

const parser = unified().use(remarkParse).use(remarkGfm);
type MarkdownNode = { type: string; value?: string; alt?: string | null; depth?: number; children?: MarkdownNode[]; data?: { hProperties?: Record<string, unknown> } };
function nodeText(node: MarkdownNode): string {
  return node.value ?? node.alt ?? node.children?.map(nodeText).join('') ?? '';
}
function markdownHeadings(content: string): { text: string; depth: number }[] {
  const headings: { text: string; depth: number }[] = [];
  function visit(node: MarkdownNode) {
    if (node.type === 'heading') headings.push({ text: nodeText(node), depth: node.depth ?? 2 });
    node.children?.forEach(visit);
  }
  visit(parser.parse(content));
  return headings;
}

// The Markdown renderer uses the same block/step prefixes for unique anchors.
export function getWikiHeadings(blocks: WikiContentBlock[]): WikiHeading[] {
  return blocks.flatMap((block, index) => {
    const prefix = `wiki-${index}`;
    if (block.type === 'image' || block.type === 'model') return [];
    if (block.type === 'steps') return block.items.flatMap((item, step) => [
      { id: `${prefix}-step-${step}`, text: item.title, depth: 2 },
      ...markdownHeadings(item.text).map((heading, n) => ({ ...heading, id: `${prefix}-step-${step}-heading-${n}` })),
    ]);
    return markdownHeadings(block.text).map((heading, n) => ({ ...heading, id: `${prefix}-heading-${n}` }));
  });
}

export function wikiPlainText(blocks: WikiContentBlock[]): string {
  return blocks.map((block) => {
    if (block.type === 'image' || block.type === 'model') return block.caption;
    if (block.type === 'steps') return block.items.map((item) => `${item.title} ${item.text}`).join(' ');
    return block.text;
  }).join(' ');
}

export function wikiSlug(title: string): string {
  const letters: Record<string, string> = { а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya' };
  return [...title.toLowerCase()].map((char) => letters[char] ?? char).join('').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 96).replace(/-$/, '');
}

export function wikiHeadingPlugin(prefix: string) {
  return () => (tree: MarkdownNode) => {
    let index = 0;
    function visit(node: MarkdownNode) {
      if (node.type === 'heading') node.data = { ...node.data, hProperties: { id: `${prefix}-heading-${index++}` } };
      node.children?.forEach(visit);
    }
    visit(tree);
  };
}
